import React from 'react';
import { Plus } from 'lucide-react';

import type { DashboardGreeting, DashboardPeriod } from '../types';
import { DashboardPeriodToggle } from './DashboardPeriodToggle';

type DashboardHeaderProps = {
  greeting: DashboardGreeting;
  period: DashboardPeriod;
  onPeriodChange: (period: DashboardPeriod) => void;
  onCreateProject?: () => void;
  /** Beschriftung des Anlegen-Buttons (Standard: „Projekt“). */
  createLabel?: string;
  showAppointmentsToday: boolean;
  refreshing?: boolean;
  error?: string | null;
  lastUpdatedAt?: number | null;
};

function capitalize(value: string): string {
  return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}

function timeOfDayGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 11) return 'Guten Morgen';
  if (hour < 18) return 'Guten Tag';
  return 'Guten Abend';
}

function relativeUpdatedLabel(lastUpdatedAt: number | null | undefined): string {
  if (!lastUpdatedAt) return 'Noch nicht aktualisiert';
  const diffSec = Math.max(0, Math.floor((Date.now() - lastUpdatedAt) / 1000));
  if (diffSec < 10) return 'gerade eben aktualisiert';
  if (diffSec < 60) return `vor ${diffSec}s aktualisiert`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `vor ${diffMin} Min. aktualisiert`;
  const diffHours = Math.floor(diffMin / 60);
  return `vor ${diffHours} Std. aktualisiert`;
}

export function DashboardHeader({
  greeting,
  period,
  onPeriodChange,
  onCreateProject,
  createLabel = 'Projekt',
  showAppointmentsToday,
  refreshing = false,
  error = null,
  lastUpdatedAt = null,
}: DashboardHeaderProps) {
  const name = greeting.firstName ? capitalize(greeting.firstName) : '';
  const title = name ? `${timeOfDayGreeting()}, ${name}` : timeOfDayGreeting();
  const [, setNowTick] = React.useState(0);

  React.useEffect(() => {
    if (!lastUpdatedAt) return;
    const timer = window.setInterval(() => setNowTick((n) => n + 1), 15_000);
    return () => window.clearInterval(timer);
  }, [lastUpdatedAt]);

  const subParts = [
    capitalize(greeting.dateLabel),
    showAppointmentsToday
      ? `${greeting.appointmentsToday} ${greeting.appointmentsToday === 1 ? 'Termin' : 'Termine'} heute`
      : null,
  ].filter(Boolean);
  const syncText = refreshing
    ? 'Aktualisiere Live-Stand...'
    : error && lastUpdatedAt
      ? `${relativeUpdatedLabel(lastUpdatedAt)} · letzter Abruf fehlgeschlagen`
      : `${relativeUpdatedLabel(lastUpdatedAt)} · Auto-Refresh aktiv`;
  const syncClass = error && !refreshing ? 'text-amber-600' : 'text-slate-400';

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-lg font-bold text-app-text sm:text-xl sm:truncate">{title}</h1>
        <p className="mt-1 text-[11px] leading-5 text-app-text-subtle sm:text-xs sm:leading-normal">{subParts.join(' · ')}</p>
        <p className={`mt-1 text-[11px] leading-5 sm:text-xs sm:leading-normal ${syncClass}`}>{syncText}</p>
      </div>
      <div className="flex items-stretch gap-2 shrink-0 sm:items-center">
        <DashboardPeriodToggle value={period} onChange={onPeriodChange} />
        {onCreateProject ? (
          <button
            type="button"
            onClick={onCreateProject}
            className="inline-flex h-9 shrink-0 items-center justify-center gap-2 rounded-md bg-pipedrive-green px-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#218838]"
          >
            <Plus className="h-4 w-4" />
            {createLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
